import { SkinInfoService } from './skinInfo';
import { DeviceScreenRenderingService } from './deviceScreenRenderingService';
import { ButtonInformation, DataCallbackType, IDeviceCanvas, IDeviceContextHandler, ISkinInformation } from '../mcrc-api';
import { RcStore } from './rcStore';
import { DeviceContext, IDeviceContextScreen } from '../context/deviceContext';
import { FeatureToggleService } from '@nx-fe/services'
import { MouseEventService } from './mouseEventService';
import { ScreenBoundsService } from './screenBoundsService';
import { RcContext } from '../context/rcContext';

export class ClientArea {
  readonly cx: number;
  readonly cy: number;
  readonly width: number;
  readonly height: number;

  constructor(cx: number = 0, cy: number = 0, width: number = 0, height: number = 0) {
    this.cx = cx;
    this.cy = cy;
    this.width = width;
    this.height = height;
  }
}

export class DeviceSkinController {
  private skinInfo: ISkinInformation;
  private skinButtonList: ButtonInformation[];
  private canvas: IDeviceCanvas;
  private skinId: string = null;
  private skinInfoService: SkinInfoService;
  private rcStore: RcStore;
  private featureToggleService: FeatureToggleService;
  private screenBoundsService: ScreenBoundsService

  constructor(
    private deviceName: string,
    private skinLoadedCallback: DataCallbackType,
    private context: DeviceContext,
    private deviceContextHandler: IDeviceContextHandler,
  ) {
    this.canvas = this.context.canvas;
    this.skinId = null;
    this.skinInfoService = RcContext.instance.getService(SkinInfoService);
    this.rcStore = RcContext.instance.getService(RcStore);
    this.featureToggleService = RcContext.instance.getService(FeatureToggleService);
    this.screenBoundsService = RcContext.instance.getService(ScreenBoundsService);
    this.skinLoadedCallback = skinLoadedCallback;
    this.skinInfoService.deviceName = deviceName;
    this.load(deviceName, this.setScreenDimensionFallback);
  }

  private skinImageOnLoad = (image: Event) => {
    const data = {
      offsetX: this.skinInfo.screen.left,
      offsetY: this.skinInfo.screen.top,
      image: null
    };

    if (this.skinInfo.rotation !== 0) {
      const tmpCanvas: HTMLCanvasElement = document.createElement("canvas");
      tmpCanvas.width = this.skinInfo.deviceWidth;
      tmpCanvas.height = this.skinInfo.deviceHeight;

      DeviceScreenRenderingService.resizeCanvasToTransformed(tmpCanvas, image.target as HTMLCanvasElement, this.skinInfo.rotation, 1);
      const canvasArea: ClientArea = <any>tmpCanvas as ClientArea;
      (<any>tmpCanvas).size = new ClientArea(canvasArea.cx, canvasArea.cy, tmpCanvas.width, tmpCanvas.height);
      DeviceScreenRenderingService.drawTransformedToCanvas(tmpCanvas, image.target as HTMLCanvasElement, 0, 0, this.skinInfo.rotation, 1);
      data.image = tmpCanvas;
    } else {
      data.image = image.target;
    }

    const localStoreKey = 'image.' + this.skinId;
    this.rcStore.storeImage(localStoreKey, image.target as HTMLImageElement);

    this.deviceContextHandler.updateWithSkinInformation(this.skinInfo, data.image);
    this.skinLoadedCallback(data);
  }

  private skinImageOnError = (image: Event) => {
    this.skinLoadedCallback(null);
  }

  private setScreenDimensionFallback = () => {
    // There is no skin for this device, configure the context accordingly
    this.deviceContextHandler.updateWithSkinInformation(null, null);
    this.context.screen = {
      dw: this.context.deviceWidth,
      dh: this.context.deviceHeight,
      left: 0,
      top: 0,
      right: this.context.deviceWidth,
      bottom: this.context.deviceHeight,
      rotation: 0,
    } as IDeviceContextScreen;
    this.skinLoadedCallback(null);
  }

  private checkIfSkinImageExistOnServer = (
    skinImageFileUrl: string,
    successCallback: () => void,
    errorCallback: () => void
  ) => {
    let xhr = new XMLHttpRequest();
    xhr.open('HEAD', skinImageFileUrl, true);

    const self = this;
    xhr.onload = (data) => {
      if (xhr.status !== 200) {
        if (self.featureToggleService.isEnabled("DeveloperMode")) {
          console.log('Skin Image', self.skinId, 'does not exists on the skin server');
        }
        errorCallback();
      }
      else {
        if (self.featureToggleService.isEnabled("DeveloperMode")) {
          console.log('Skin Image', self.skinId, 'exists on the skin server');
        }
        successCallback();
      }
    };

    xhr.onerror = () => {
      if (self.featureToggleService.isEnabled("DeveloperMode")) {
        console.log('Skin Image', self.skinId, 'does not exists on the skin server');
      }
      errorCallback();
    };

    xhr.send();
  };


  private load(deviceName: string, skinLoadedCallback: DataCallbackType) {
    this.skinInfoService.loadButtonInfo(
      deviceName,
      (skinInformation) => {
        this.skinInfoLoaded(skinInformation, skinLoadedCallback);
      },
      this.context,
      this.deviceContextHandler
    );
  }

  private skinInfoLoaded = (skinInformation: ISkinInformation, skinLoadedCallback: DataCallbackType) => {
    if (skinInformation?.id) {
      this.skinInfo = skinInformation;
      this.skinId = this.skinInfo.id;
      this.skinButtonList = skinInformation.buttonIndexes;

      // if (skinInformation.skin3d !== undefined && skinInformation.skin3d.buttonIndexes !== undefined) {
      //   const skin3dButtonList = skinInformation.skin3d.buttonIndexes;
      // }

      const skinImage = new Image();
      skinImage.onload = this.skinImageOnLoad;
      skinImage.onerror = this.skinImageOnError;

      const localStoreKey = 'image.' + this.skinId;
      const localStoreImage = this.rcStore.loadImage(localStoreKey);
      let skinImageSrc = '';

      if (localStoreImage == null) {
        skinImage.crossOrigin = 'anonymous';
        const skinImageName = skinInformation.hasNotchImage ? this.skinId + '.T' : this.skinId;
        skinImageSrc = `https://skinsapi.soti.net/images/skins/${skinImageName}.png`;
      } else {
        skinImageSrc = localStoreImage;
      }

      this.checkIfSkinImageExistOnServer(skinImageSrc, () => {
        skinImage.src = skinImageSrc;
      }, () => {
        this.setScreenDimensionFallback();
      });
    } else {
      this.setScreenDimensionFallback();
    }
  }

  public checkIfWithinScreenSurface = (callback: (success: boolean, event: Event) => void, event) => {
    //NOTE: On Chrome when mouse is down cursor may not update sometimes
    // https://bugs.webkit.org/show_bug.cgi?id=53341
    // No workaround found so far
    const isIrregularSkin = this.deviceContextHandler.currentContext().skin.isIrregular;
    if (this.screenBoundsService.isWithinScreenBounds(this.context, event, isIrregularSkin)) {
      callback(true, event);
    } else {
      callback(false, event);
    }
  }

  public checkIfMouseIsOverAnyButton = (callback: (...args) => void, event: MouseEvent): boolean => {
    if (this.skinButtonList != undefined) {
      MouseEventService.fixEvent(event);
      const rotationInRadians = MouseEventService.getRotationInRadians((this.context.rotation + this.context.clientSideRotation) & 3);
      var mouseX = MouseEventService.getTransformedMousePositionX(event, rotationInRadians, this.context.scale);
      var mouseY = MouseEventService.getTransformedMousePositionY(event, rotationInRadians, this.context.scale);
      mouseX = Math.ceil(mouseX);
      mouseY = Math.ceil(mouseY);

      for (var i = 0; i < this.skinButtonList.length; i++) {
        if (this.skinButtonList[i].top <= mouseY &&
          this.skinButtonList[i].left <= mouseX &&
          this.skinButtonList[i].bottom >= mouseY &&
          this.skinButtonList[i].right >= mouseX) {
          callback(this.skinButtonList[i]);
          return true;
        }
      }
    }
    return false;
  }

  public onClick = (onClickCallback, event: MouseEvent) => this.checkIfMouseIsOverAnyButton(onClickCallback, event);

  private hover = () => {
    if (!RcContext.instance.security.RemoteControlInput || this.context.disableMouseInput) {
      this.canvas.element.style.cursor = "disable";
    }
    else {
      this.canvas.element.style.cursor = "pointer";
    }
    return true;
  };

  public onHover = (event: MouseEvent) => {
    let disableCursorInAnnotations = false;
    let buttonHovered = this.checkIfMouseIsOverAnyButton(this.hover, event);

    this.checkIfWithinScreenSurface((withinScreenSurface, event: MouseEvent) => {
      var mouseX = event.offsetX || (event.pageX - (<HTMLElement>event.currentTarget).offsetLeft);
      var mouseY = event.offsetY || (event.pageY - (<HTMLElement>event.currentTarget).offsetTop);
      if (withinScreenSurface) {
        this.context.cursorX = mouseX * this.context.scaleFactor;
        this.context.cursorY = mouseY * this.context.scaleFactor;
        disableCursorInAnnotations = this.context.annotationsToolHint && RcContext.instance.security.RemoteControlInput;
      } else {
        // This will ensure the drawing hint disappears
        this.context.cursorX = undefined;
        this.context.cursorY = undefined;
        disableCursorInAnnotations = false;
      }
      RcContext.instance.deviceScreenTransformService.refreshCanvas();
    }, event);

    if (!buttonHovered) {
      if (disableCursorInAnnotations) {
        // Suspend cursor if we are rendering annotation tool hint
        // on the canvas
        this.canvas.element.style.cursor = "none";
      } else if (this.context.disableMouseInput) {
        // Show Blocker cursor when mouse input is explicitly disabled
        this.canvas.element.style.cursor = "not-allowed";
      } else {
        // Remove explicit style for the cursor, that will allow the CSS for cursor to take effect in.
        // e.g.:  #rc_canvas.no-input style which is set in remoteControlCanvas.tpl
        this.canvas.element.style.cursor = "";
      }
    }
  }

  public getDeviceInfo = (): ISkinInformation => { return this.skinInfo; }
}
